/**
 * Sophie Chat Context
 * Global state management for chat history and current session
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { sophieChatService, SophieChatSummary } from '../services/firebase/sophieChatService';
import { ChatMessage } from '../services/sophieService';

interface SophieChatContextType {
  // Current chat state
  currentChatId: string | null;
  messages: ChatMessage[];
  isLoading: boolean;
  
  // Chat list
  chats: SophieChatSummary[];
  isLoadingChats: boolean;
  
  // Actions
  startNewChat: () => void;
  loadChat: (chatId: string) => Promise<void>;
  addMessage: (message: ChatMessage) => Promise<ChatMessage[]>;
  deleteChat: (chatId: string) => Promise<void>;
  refreshChats: () => Promise<void>;
}

const SophieChatContext = createContext<SophieChatContextType | undefined>(undefined);

export const useSophieChat = () => {
  const context = useContext(SophieChatContext);
  if (!context) {
    throw new Error('useSophieChat must be used within a SophieChatProvider');
  }
  return context;
};

export const SophieChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  // Current chat state - use ref for chatId to avoid stale closure
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const currentChatIdRef = React.useRef<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesRef = React.useRef<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // **CLIENT-SIDE CACHE for instant chat switching**
  // Stores chat messages in memory after first load
  const chatCacheRef = React.useRef<Map<string, ChatMessage[]>>(new Map());
  
  // Keep refs in sync with state
  React.useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);
  
  React.useEffect(() => {
    messagesRef.current = messages;
    // Also update cache when messages change
    if (currentChatIdRef.current) {
      chatCacheRef.current.set(currentChatIdRef.current, messages);
    }
  }, [messages]);
  
  // Chat list state
  const [chats, setChats] = useState<SophieChatSummary[]>([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);

  // Load user's chats on mount
  const refreshChats = useCallback(async () => {
    if (!user?.uid) return;
    
    setIsLoadingChats(true);
    try {
      const userChats = await sophieChatService.getChats(user.uid);
      setChats(userChats);
    } catch (error) {
      console.error('Failed to load chats:', error);
    } finally {
      setIsLoadingChats(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (user?.uid) {
      refreshChats();
    } else {
      // Clear state on logout
      setChats([]);
      setMessages([]);
      setCurrentChatId(null);
      chatCacheRef.current.clear(); // Clear cache on logout
    }
  }, [user?.uid, refreshChats]);

  // Start a new chat
  const startNewChat = useCallback(() => {
    setCurrentChatId(null);
    currentChatIdRef.current = null;
    setMessages([]);
    messagesRef.current = [];
  }, []);

  // Load an existing chat - OPTIMIZED with cache-first for instant switching
  const loadChat = useCallback(async (chatId: string) => {
    if (chatId === currentChatIdRef.current) return;
    
    // Check cache first for INSTANT switch (no loading state)
    const cachedMessages = chatCacheRef.current.get(chatId);
    if (cachedMessages) {
      // Instant switch using cached data
      setCurrentChatId(chatId);
      currentChatIdRef.current = chatId;
      setMessages(cachedMessages);
      messagesRef.current = cachedMessages;
      
      // Background refresh to ensure data freshness (silent, no loading indicator)
      sophieChatService.getChat(chatId).then(chat => {
        if (chat && chatId === currentChatIdRef.current) {
          // Only update if still on this chat and data differs
          if (chat.messages.length !== cachedMessages.length) {
            setMessages(chat.messages);
            messagesRef.current = chat.messages;
            chatCacheRef.current.set(chatId, chat.messages);
          }
        }
      }).catch(err => console.error('Background refresh failed:', err));
      
      return;
    }
    
    // First-time load: show loading state, fetch from Firestore
    setIsLoading(true);
    try {
      const chat = await sophieChatService.getChat(chatId);
      if (chat) {
        setCurrentChatId(chatId);
        currentChatIdRef.current = chatId;
        setMessages(chat.messages);
        messagesRef.current = chat.messages;
        // Cache for future instant access
        chatCacheRef.current.set(chatId, chat.messages);
      }
    } catch (error) {
      console.error('Failed to load chat:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Add a message to current chat - returns the updated messages array
  const addMessage = useCallback(async (message: ChatMessage): Promise<ChatMessage[]> => {
    if (!user?.uid) return messagesRef.current;
    
    // Update messages immediately (using ref for latest)
    const updatedMessages = [...messagesRef.current, message];
    setMessages(updatedMessages);
    messagesRef.current = updatedMessages;
    
    try {
      // Get chatId from ref (always current)
      const chatId = currentChatIdRef.current;
      
      if (!chatId) {
        // Create new chat with first message
        const newChatId = await sophieChatService.createChat(user.uid, message);
        
        // Update both state and ref
        setCurrentChatId(newChatId);
        currentChatIdRef.current = newChatId;
        
        // Optimistically add new chat to list (at the top, with slide-in animation)
        const newChatSummary: SophieChatSummary = {
          id: newChatId,
          title: message.content.substring(0, 35) + (message.content.length > 35 ? '...' : ''),
          updatedAt: new Date(),
          messageCount: 1
        };
        setChats(prev => [newChatSummary, ...prev]);
      } else {
        // Update existing chat (save in background, no refresh needed)
        await sophieChatService.updateChat(chatId, updatedMessages);
        
        // Optimistically update timestamp locally (move to top)
        setChats(prev => {
          const updated = prev.map(c => 
            c.id === chatId 
              ? { ...c, updatedAt: new Date(), messageCount: updatedMessages.length }
              : c
          );
          // Move active chat to top
          const activeChat = updated.find(c => c.id === chatId);
          const others = updated.filter(c => c.id !== chatId);
          return activeChat ? [activeChat, ...others] : updated;
        });
      }
    } catch (error) {
      console.error('Failed to save message:', error);
    }
    
    return updatedMessages;
  }, [user?.uid]);

  // Delete a chat
  const deleteChat = useCallback(async (chatId: string) => {
    try {
      await sophieChatService.deleteChat(chatId);
      
      // Remove from cache
      chatCacheRef.current.delete(chatId);
      
      // If deleting current chat, start fresh
      if (chatId === currentChatIdRef.current) {
        startNewChat();
      }
      
      // Refresh chat list
      await refreshChats();
    } catch (error) {
      console.error('Failed to delete chat:', error);
    }
  }, [startNewChat, refreshChats]);

  const value: SophieChatContextType = {
    currentChatId,
    messages,
    isLoading,
    chats,
    isLoadingChats,
    startNewChat,
    loadChat,
    addMessage,
    deleteChat,
    refreshChats
  };

  return (
    <SophieChatContext.Provider value={value}>
      {children}
    </SophieChatContext.Provider>
  );
};

